import {SoiRegistrationInfo, TrunkingSite} from "../../app/types";
import {FormSection} from "../../common/components/SectionedForm";
import {useTranslation} from "react-i18next";
import React, {ReactNode, useCallback} from "react";
import {api} from "../../common/api";
import {useFetchData} from "../../common/servercall/useFetchData";
import {LabelValues, LabelValueType} from "../../common/components/LabelValues";
import PaperStack from "../../common/form/PaperStack";
import FormGroup from "../../common/form/FormGroup";
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import axios from "axios";
import {convertDateToISO} from "../../common/util/formatters";

export const useSoiRegistrationInfoSection = (trunkingSite: TrunkingSite | undefined) : FormSection<SoiRegistrationInfo> => {
    const { t } = useTranslation()

    const fetchConfig = useCallback(
        async (productId: string | undefined): Promise<SoiRegistrationInfo | undefined> => {
            if (!productId) return
            const { data } = await api.get<SoiRegistrationInfo>(`/api/trunking/site/${productId}/soi-registration-info`)
            return data
        },
        []
    )
    const { data, isFetching, error, refetch } = useFetchData<SoiRegistrationInfo>(
        useCallback(() => fetchConfig(trunkingSite?.productId), [fetchConfig, trunkingSite?.productId])
    )

    const generalLabelValues = useCallback(
        (value: SoiRegistrationInfo): LabelValueType[] => {
            return [
                { label: t('trunking-sites.line-port'), value: <>{value.linePort}</>, type: "FULL_LINE" },
                { label: t('trunking-sites.expiration'), value: <>{convertDateToISO(value.expiration)}</> },
                { label: t('trunking-sites.public-ip'), value: <>{value.publicIp}</> },
                { label: t('trunking-sites.public-port'), value: <>{value.publicPort}</> },
                { label: t('trunking-sites.private-ip'), value: <>{value.privateIp}</> },
                { label: t('trunking-sites.private-port'), value: <>{value.privatePort}</> },
                { label: t('trunking-sites.user-agent'), value: <>{value.userAgent}</> },
                { label: t('trunking-sites.lockout-count'), value: <>{value.lockoutCount}</> },
                { label: t('trunking-sites.lockout-started'), value: <>{convertDateToISO(value.lockoutStarted)}</> },
                { label: t('trunking-sites.lockout-expires'), value: <>{convertDateToISO(value.lockoutExpires)}</> },
            ]}, [t]
    )

    const renderSoiRegistrationInfo = useCallback(
        (soiRegistrationInfo: SoiRegistrationInfo): ReactNode => {
            return (
                <PaperStack>
                    <FormGroup key={1} fullWidth={true}>
                        <LabelValues labelValues={generalLabelValues(soiRegistrationInfo)} />
                    </FormGroup>
                    <></>
                </PaperStack>
            )
        },
        [generalLabelValues]
    )

    return {
        title: t('trunking-sites.section.registration-info'),
        data: data,
        isFetching: isFetching,
        error: !!error ? axios.isAxiosError(error) && error.response?.status === 404? `trunking-sites.no-registration-found` : error : null,
        refetch: refetch,
        renderDetails: renderSoiRegistrationInfo,
        renderForm: null,
        enabled: () => true,
        icon: () => <AppRegistrationIcon />
    }
}